﻿import  { GenderEnum, UserAccountStatusEnum, VerificationTypesEnum } from './enum';
import type { To } from 'react-router-dom';
import {
	AccountingUserStatusEnum,
	AccountingUserTypeEnum,
	SortEnum,
	TransactionTypeEnum,
	TransactionCategoryEnum,
} from '@/enums';
import dayjs from 'dayjs';

const routes = {
	nav: {
		home: '/',
		login: '/login',
		marketing: {
			path: '/marketing',
			messages: { path: 'messages', getHref: () => '/marketing/messages' },
			promotions: {
				path: 'promotions',
				getHref: () => '/marketing/promotions',
				create: { path: '/marketing/promotions/create', getHref: () => '/marketing/promotions/create' },
			},
			promoCodes: { path: 'promo-codes', getHref: () => '/marketing/promo-codes' },
			bulkNotifications: { path: 'bulk-notifications', getHref: () => '/marketing/bulk-notifications' },
		},
		events: {
			path: '/events',
			getHref: () => '/events',
			create: { path: '/events/create', getHref: () => '/events/create' },
		},
		referral: '/referral',
		tools: '/tools',
		bioReview: '/bio',
		reportConversation: {
			path: '/reports/conversation/:conversationId',
			getHref: (conversationId: string, reporterUserId?: string, reportedUserId?: string) => {
				const params = new URLSearchParams();
				if (reporterUserId) params.set('reporterUserId', reporterUserId);
				if (reportedUserId) params.set('reportedUserId', reportedUserId);
				const qs = params.toString();
				return `/reports/conversation/${conversationId}${qs ? `?${qs}` : ''}`;
			},
		},
		reports: {
			base: '/reports',
			getHref: (queryParams: {
				type?: VerificationTypesEnum;
				tab?: 'users' | 'verifiers';
				source?: 'all' | 'member' | 'automatic' | 'refund';
			}) => {
				const search = new URLSearchParams({
					...queryParams,
					tab: queryParams?.tab ?? 'users',
				} as any).toString();

				return {
					pathname: routes.nav.reports.base,
					search,
				} as To;
			},
		},
		verificationsHistory: {
			path: '/verifications/history/:type',
			getHref: (
				type: VerificationTypesEnum,
				queryParams?: {
					page?: number;
					date?: Date | string;
					sortType?: SortEnum;
					authorIds?: string[];
				},
			) => {
				const pathname = routes.nav.verificationsHistory.path
					.replace(':type', type)
					.toLowerCase();
				const urlSearchParams = new URLSearchParams({
					...queryParams,
					date: dayjs(queryParams?.date).format('YYYY-MM-DD'),
					sortType: queryParams?.sortType ?? SortEnum.DESC,
					page: queryParams?.page ?? 1,
				} as any);

				if (!queryParams?.authorIds) {
					urlSearchParams.delete('authorIds');
				}

				return {
					pathname,
					search: urlSearchParams.toString(),
				} as To;
			},
		},
		certifications: '/certifications',
		verifications: '/verifications',
		users: {
			path: '/users',
			getHref: (queryParams?: {
				gender?: GenderEnum[];
				accountStatus?: UserAccountStatusEnum[];
			}) => {
				const accountStatuses = queryParams?.accountStatus ?? [
					UserAccountStatusEnum.ACTIVE,
					UserAccountStatusEnum.PAUSED,
				];
				const genders = queryParams?.gender ?? [GenderEnum.MALE, GenderEnum.FEMALE];

				const urlSearchParams = new URLSearchParams();
				accountStatuses.forEach(l => {
					urlSearchParams.append('accountStatus', l);
				});
				genders.forEach(l => {
					urlSearchParams.append('gender', l);
				});

				return {
					pathname: routes.nav.users.path,
					search: urlSearchParams.toString(),
				} as To;
			},
			single: {
				path: '/users/:userId',
				getHref: (userId: string) => `/users/${userId}`,
				subAccounts: {
					getHref: (userId: string) => `/users/${userId}/sub-accounts`,
					path: '/users/:userId/sub-accounts',
				},
			},
		},
		accounting: {
			path: 'accounting',
			overview: {
				path: 'overview',
				getHref: () =>
					[routes.nav.accounting.path, routes.nav.accounting.overview.path].join('/'),
			},
			transactions: {
				path: 'transactions',
				getHref: (queryParams?: {
					type?: TransactionTypeEnum[];
					category?: TransactionCategoryEnum[];
				}) => {
					const types = queryParams?.type ?? [
						TransactionTypeEnum.INCOME,
						TransactionTypeEnum.EXPENSE,
					];
					const categories = queryParams?.category ?? [
						TransactionCategoryEnum.SALARY,
						TransactionCategoryEnum.INVESTOR,
						TransactionCategoryEnum.IN_APP_PURCHASE,
						TransactionCategoryEnum.MARKETING,
						TransactionCategoryEnum.MARKETING_GOVOIR,
						TransactionCategoryEnum.ADMINISTRATION,
						TransactionCategoryEnum.INFRASTRUCTURE,
						TransactionCategoryEnum.BANK_EXPENSE,
						TransactionCategoryEnum.OTHER_INCOME,
						TransactionCategoryEnum.OTHER,
					];

					const urlSearchParams = new URLSearchParams();
					types.forEach(t => {
						urlSearchParams.append('type', t);
					});
					categories.forEach(c => {
						urlSearchParams.append('category', c);
					});

					return {
						pathname: routes.nav.accounting.transactions.path,
						search: urlSearchParams.toString(),
					};
				},
			},
			users: {
				path: 'users',
				getHref: (queryParams?: {
					type?: AccountingUserTypeEnum[];
					status?: AccountingUserStatusEnum[];
				}) => {
					const types = queryParams?.type ?? [
						AccountingUserTypeEnum.INVESTOR,
						AccountingUserTypeEnum.EMPLOYEE,
					];
					const statuses = queryParams?.status ?? [AccountingUserStatusEnum.ACTIVE];

					const urlSearchParams = new URLSearchParams();
					types.forEach(t => {
						urlSearchParams.append('type', t);
					});
					statuses.forEach(s => {
						urlSearchParams.append('status', s);
					});

					return {
						pathname: routes.nav.accounting.users.path,
						search: urlSearchParams.toString(),
					};
				},
			},
		},
		settings: {
			path: 'settings',
			permissions: {
				path: 'permissions',
				getHref: () =>
					['', routes.nav.settings.path, routes.nav.settings.permissions.path].join('/'),
			},
			tools: {
				path: 'tools',
				getHref: () =>
					['', routes.nav.settings.path, routes.nav.settings.tools.path].join('/'),
			},
			auditLogs: {
				path: 'audit-logs',
				getHref: () =>
					['', routes.nav.settings.path, routes.nav.settings.auditLogs.path].join('/'),
			},
			paymentProviders: {
				path: 'payment-providers',
				getHref: () =>
					['', routes.nav.settings.path, routes.nav.settings.paymentProviders.path].join('/'),
			},
			paymentPackages: {
				path: 'payment-packages',
				getHref: () =>
					['', routes.nav.settings.path, routes.nav.settings.paymentPackages.path].join('/'),
			},
			paymentMethodRequests: {
				path: 'payment-method-requests',
				getHref: () =>
					['', routes.nav.settings.path, routes.nav.settings.paymentMethodRequests.path].join('/'),
			},
		},
	},
	links: {
		analytics: 'https://analytics.kesylo.us/heyamapp',
		plunk: 'https://plunk.kesylo.us',
		shlink: 'https://app.shlink.io',
		ntfy: 'https://notify.kesylo.us',
		beszel: 'https://beszel.kesylo.us',
		metabase: 'https://stats.kesylo.us',
		coolify: 'https://coolify.kesylo.us/',
		sentry: 'https://sentry-master.kesylo.us/',
		semaphore: 'https://ansible.kesylo.us/project/1/',
		uptimeKuma: 'https://uptimekuma.kesylo.us/dashboard',
	},
	api: {
		login: '/admin/login',
		getConnectedUser: '/user/me',
		searchUser: '/admin/user/search',
		verifyBio: '/admin/verification/bio',
		dashboard: '/admin/dashboard/cards',
		getDuplicateAccounts: '/admin/user/duplicates',
		deleteProfilePicture: '/admin/user/delete/picture',
		transactions: '/admin/accounting/transactions',
		accountingSummary: '/admin/accounting/summary',
		auditLogs: '/admin/audit-logs',
		payTransaction: (id: string) => `/admin/accounting/transactions/${id}/status`,
		finishCertification: '/admin/verification/id',
		subAccounts: '/admin/user/sub-accounts',
		editEmployeePermissions: '/admin/add',
		accountingUsers: '/admin/accounting/users',
		referrals: '/admin/user/referral',
		paymentMethodRequests: '/admin/payment/method-request',
		reports: {
			ignore: '/admin/report/ignore',
			base: '/admin/report',
			conversation: (conversationId: string) => `/admin/report/conversation/${conversationId}`,
		},
		employees: {
			editPermissions: (employeeId: string) =>
				`/admin/employees/${employeeId}/permissions`,
			base: '/admin/employees',
		},
		dms: '/admin/dms',
		users: {
			transactions: (userId: string) => `/admin/users/${userId}/transactions`,
			payments: (userId: string) => `/admin/users/${userId}/payments`,
			setCoins: (userId: string) => `/admin/users/${userId}/coins`,
			addIncognito: (userId: string) => `/admin/users/${userId}/incognito`,
			addBoost: (userId: string) => `/admin/users/${userId}/boost`,
			delete: `/admin/user/delete`,
			edit: '/admin/user/update',
			detailsTabsStats: (userId: string) => `/admin/user/${userId}/details-tabs-stats`,
			pushes: (userId: string) => `/admin/user/${userId}/pushes`,
			find: '/admin/user/find',
			warn: '/admin/user/warn',
			ban: '/admin/user/ban',
			suspend: '/admin/user/suspend',
			unsuspend: '/admin/user/unsuspend',
			base: '/admin/users',
		},
		certifications: {
			base: '/admin/certifications',
			actions: '/admin/verification/id'
		},
		verifications: {
			completed: '/admin/verification/completed',
			count: '/admin/verifications/count',
			photo: '/admin/verification/photo',
			bio: '/admin/verification/bio',
			base: '/admin/verifications',
		},
		addAdminUser: '/admin/add',
		geolocationSearch: '/geolocations/search',
		adminMessages: '/admin/message/send',
		promotions: {
			base: '/admin/promotions',
			create: '/admin/promotions',
			updateStatus: (id: string) => `/admin/promotions/${id}/status`,
			delete: (id: string) => `/admin/promotions/${id}`,
		},
		promoCodes: {
			base: '/admin/promo-codes',
			byId: (id: string) => `/admin/promo-codes/${id}`,
			disabled: (id: string) => `/admin/promo-codes/${id}/disabled`,
			duplicate: (id: string) => `/admin/promo-codes/${id}/duplicate`,
			stats: (id: string) => `/admin/promo-codes/${id}/stats`,
			usages: (id: string) => `/admin/promo-codes/${id}/usages`,
			announce: (id: string) => `/admin/promo-codes/${id}/announce`,
		},
		events: {
			base: '/admin/events',
			create: '/admin/events',
			delete: (id: string) => `/admin/events/${id}`,
		},
		adminMessagesList: '/admin/message/list',
		adminMessageById: (id: string) => `/admin/message/${id}`,
		cancelAdminMessage: (id: string) => `/admin/message/${id}/cancel`,
		paymentProviders: '/admin/payment/providers',
		coinPackages: '/admin/payment/coin/packages',
		incognitoPackages: '/admin/payment/incognito/packages',
		boostPackages: '/admin/payment/boost/packages',
		bulkNotificationCampaigns: {
			base: '/admin/bulk-notification/campaigns',
			cancel: (id: string) => `/admin/bulk-notification/campaigns/${id}/cancel`,
		},
		pushUninstalls: {
			base: '/admin/push/uninstalled',
			check: '/admin/push/uninstalled/check',
			hideFromDecks: '/admin/push/uninstalled/hide-from-decks',
		},
		createPaymentOperator: '/admin/payment/operator',
		updatePaymentOperator: (operator: string) => `/admin/payment/operator/${operator}`,
		deletePaymentOperator: (operator: string) => `/admin/payment/operator/${operator}`,
	},
};

export default routes;

